import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { QuestAccent } from '../../daily-quests-page.constants';
import { IQuest, NextTimeStatus, QuestPeriod, QuestTypes } from '../../daily-quests-page.types';
import clsx from 'clsx';

import { ImageData, OptimizedImage } from 'shared/components/ui/optimized-image/optimized-image';

import attention from 'assets/png/!.png?format=webp;avif&as=picture';
import chestShadow from 'assets/png/daily-quests/chest-shadow.png?format=webp;avif&as=picture';
import dailyLogInImage from 'assets/png/daily-quests/daily-log-in-image.png?format=webp;avif&as=picture';
import dailyPlayImage from 'assets/png/daily-quests/daily-play-image.png?format=webp;avif&as=picture';
import dailyWinImage from 'assets/png/daily-quests/daily-win-image.png?format=webp;avif&as=picture';
import rainbowImage from 'assets/png/daily-quests/rainbow-chest.png?format=webp;avif&as=picture';
import weaklyStreakImage from 'assets/png/daily-quests/weakly-streak-image.png?format=webp;avif&as=picture';
import starIcon from 'assets/png/star.png?format=webp;avif&as=picture';

interface IChestIconProps {
  variant: QuestAccent;
  points: number;
  quest: IQuest;
  className?: string;
  disabled?: boolean;
  isPrizeCrate?: boolean;
}

export const ChestIcon: FC<IChestIconProps> = ({
  variant,
  points,
  quest,
  className,
  disabled,
  isPrizeCrate,
}) => {
  const { t } = useTranslation();
  const imageByVariant: Record<QuestAccent, ImageData> = {
    [QuestAccent.Bronze]: dailyLogInImage,
    [QuestAccent.Silver]: dailyPlayImage,
    [QuestAccent.Gold]: dailyWinImage,
    [QuestAccent.Rainbow]: rainbowImage,
    [QuestAccent.Ice]: weaklyStreakImage,
  };

  const canClaim = useMemo(
    () => quest.id === QuestTypes.Daily && quest.status === NextTimeStatus.Ready && !disabled,
    [quest.id, quest.status, disabled],
  );

  const isWeeklyDone = useMemo(
    () =>
      quest.frequency === QuestPeriod.Weekly &&
      !!quest.progress &&
      quest.progress.current >= quest.progress.target,
    [quest.frequency, quest.progress],
  );

  const highlight = useMemo(() => {
    if (disabled) return false;
    if (quest.id === QuestTypes.Daily) return canClaim;
    if (quest.frequency === QuestPeriod.Weekly) return isWeeklyDone;
    if (quest.href) return true;
    return false;
  }, [disabled, quest.id, quest.frequency, canClaim, isWeeklyDone]);

  return (
    <div className={clsx('relative max-h-[117px] sm:max-h-[147px]', className)}>
      <div className="group relative transition-transform duration-300 hover:scale-105">
        {disabled && (
          <div className="absolute inset-0 flex items-center justify-center">
            {
              <span className="text-base font-bold text-white-text">
                {t('pages.dailyQuests.claiming')}
              </span>
            }
          </div>
        )}
        {highlight && (
          <OptimizedImage
            imageData={chestShadow}
            alt="shadow"
            className={clsx(
              'pointer-events-none absolute h-full w-full scale-150 select-none transition-all duration-500',
              'animate-scalePulse motion-reduce:animate-none',
            )}
          />
        )}

        <OptimizedImage
          imageData={imageByVariant[variant]}
          alt={`${variant} chest`}
          className={clsx(
            'relative h-[8rem] min-h-[8rem] w-[8.75rem] min-w-[8.75rem] transition-all duration-300 active:scale-90 sm:h-[10rem] sm:w-[9.75rem]',
            disabled && 'pointer-events-none opacity-50',
            highlight && [
              'animate-shake will-change-transform motion-reduce:animate-none',
              // pause the wiggle while the user hovers/presses
              'hover:[animation-play-state:paused] group-active:[animation-play-state:paused]',
            ],
          )}
          aria-disabled={disabled || undefined}
        />
      </div>

      {highlight && (
        <OptimizedImage
          imageData={attention}
          alt="attention"
          className={clsx(
            'absolute right-2.5 top-4 h-6 w-6',
            'animate-badgePop motion-reduce:animate-none',
          )}
        />
      )}

      <div className="absolute -bottom-3 left-1/2 flex min-h-7 min-w-16 -translate-x-1/2 items-center justify-center gap-1 rounded-2xl border-2 border-[#48487b] bg-gradient-to-b from-[#3C3C8A] to-[#3C3C8A] p-0.5 px-1">
        <p className="whitespace-nowrap bg-gradient-to-b from-white to-[#9DFFFF] bg-clip-text pt-[1px] text-center text-[0.875rem] font-bold leading-3 text-transparent">
          + {points}
          {isPrizeCrate && '$'}
        </p>
        {!isPrizeCrate && (
          <OptimizedImage imageData={starIcon} alt="star" className="mt-[1px] h-5 w-5" />
        )}
      </div>
    </div>
  );
};
